import React, { useState } from 'react';
import { useWallet } from './WalletContext';
import { web } from '@klever/sdk-web';
import {
  FaGavel,
  FaClock,
  FaCoins,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaInfoCircle,
  FaImage,
  FaBolt,
  FaTag,
  FaCube,
  FaMinus,
  FaPlus,
  FaTimes
} from 'react-icons/fa';
import '../styles/AuctionListingModal.css';

const AuctionListingModal = ({ nft, onClose, onSuccess }) => {
  const { useKdaFee, selectedKda } = useWallet();
  const [listingType, setListingType] = useState('fixed');
  const [price, setPrice] = useState('');
  const [startingBid, setStartingBid] = useState('');
  const [buyNowPrice, setBuyNowPrice] = useState('');
  const [duration, setDuration] = useState(7);
  const [topChoice, setTopChoice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [quantity, setQuantity] = useState(1);

  // Check if this is an SFT
  const isSFT = nft?.isSFT || (nft?.balance && nft.balance > 1);
  const maxQuantity = nft?.balance || 1;

  // Duration options
  const durationOptions = [
    { value: 1, label: '1d' },
    { value: 3, label: '3d' },
    { value: 7, label: '7d' },
    { value: 14, label: '14d' },
    { value: 30, label: '30d' },
    { value: 60, label: '60d' },
    { value: 90, label: '90d' }
  ];

  const handleQuantityChange = (newQuantity) => {
    const qty = Math.max(1, Math.min(maxQuantity, parseInt(newQuantity) || 1));
    setQuantity(qty);
  };

  const handleList = async () => {
    setErrorMsg('');

    // Validation
    if (listingType === 'fixed') {
      if (!price || parseFloat(price) <= 0) {
        setErrorMsg('Please enter a valid price');
        return;
      }
    } else {
      if (!startingBid || parseFloat(startingBid) <= 0) {
        setErrorMsg('Please enter a starting bid price');
        return;
      }
      if (buyNowPrice && parseFloat(buyNowPrice) <= parseFloat(startingBid)) {
        setErrorMsg('Buy Now price must be higher than starting bid');
        return;
      }
    }

    // SFT quantity validation
    if (isSFT) {
      if (quantity < 1 || quantity > maxQuantity) {
        setErrorMsg(`Quantity must be between 1 and ${maxQuantity}`);
        return;
      }
    }

    setLoading(true);
    setStatus('processing');

    try {
      const now = Math.floor(Date.now() / 1000);
      const durationInSeconds = duration * 24 * 60 * 60;
      const endTime = now + durationInSeconds;

      let sellContract;

      if (listingType === 'fixed') {
        sellContract = {
          type: 18,
          payload: {
            marketType: 0,
            marketplaceID: '417b70c0eb7a33cb',
            assetId: `${nft.collectionId}/${nft.nftId}`,
            currencyId: 'KLV',
            price: Math.floor(parseFloat(price) * 1000000),
            endTime: endTime
          }
        };

        if (isSFT && quantity > 1) {
          sellContract.payload.amount = quantity;
        }

        if (topChoice) {
          sellContract.data = ['TOP_CHOICE'];
        }
      } else {
        sellContract = {
          type: 18,
          payload: {
            marketType: 1,
            marketplaceID: '417b70c0eb7a33cb',
            assetId: `${nft.collectionId}/${nft.nftId}`,
            currencyId: 'KLV',
            price: Math.floor(parseFloat(startingBid) * 1000000),
            endTime: endTime
          }
        };

        if (isSFT && quantity > 1) {
          sellContract.payload.amount = quantity;
        }

        if (topChoice) {
          sellContract.data = ['TOP_CHOICE'];
        }

        if (buyNowPrice && parseFloat(buyNowPrice) > 0) {
          sellContract.payload.reservePrice = Math.floor(parseFloat(buyNowPrice) * 1000000);
        }
      }

      const txOptions = useKdaFee && selectedKda ? { kdaFee: selectedKda } : undefined;

      if (topChoice) {
        const paymentContract = {
          type: 0,
          payload: {
            amount: 50000000,
            receiver: 'klv1355u5mgxl5h8gmxzz9tjt3jpp8h6frxykvvs8zya7xrd3ghhc06qxa9kyq',
            kda: selectedKda || 'KLV'
          }
        };

        const paymentTx = await web.buildTransaction([paymentContract], [], txOptions);
        const signedPayment = await web.signTransaction(paymentTx);

        if (!signedPayment) {
          setStatus('error');
          setErrorMsg('Payment transaction was rejected');
          setLoading(false);
          return;
        }

        const paymentResult = await web.broadcastTransactions([signedPayment]);
        const paymentTxHash = paymentResult?.data?.txsHashes?.[0] || signedPayment?.hash || paymentTx?.hash;

        if (!paymentTxHash) {
          setStatus('error');
          setErrorMsg('Failed to get payment transaction hash');
          setLoading(false);
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 2000));

        const assetId = `${nft.collectionId}/${nft.nftId}`;
        const marker = `TOP_CHOICE:${paymentTxHash}:${assetId}`;
        const encoder = new TextEncoder();
        const bytes = encoder.encode(marker);
        const listingData = [Array.from(bytes)];

        const listingTx = await web.buildTransaction([sellContract], listingData, txOptions);
        const signedListing = await web.signTransaction(listingTx);

        if (!signedListing) {
          setStatus('error');
          setErrorMsg('Listing transaction was rejected');
          setLoading(false);
          return;
        }

        await web.broadcastTransactions([signedListing]);
        setStatus('success');
      } else {
        const listingTx = await web.buildTransaction([sellContract], [], txOptions);
        const signedListing = await web.signTransaction(listingTx);

        if (!signedListing) {
          setStatus('error');
          setErrorMsg('Transaction was rejected');
          setLoading(false);
          return;
        }

        await web.broadcastTransactions([signedListing]);
        setStatus('success');
      }

      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Listing error:', error);
      setStatus('error');
      setErrorMsg(error.message || 'Failed to list NFT');
    } finally {
      setLoading(false);
    }
  };

  const calculateEndDate = () => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + duration);
    return endDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fullpage-modal-overlay" onClick={onClose}>
      <div className="fullpage-modal" onClick={(e) => e.stopPropagation()}>
        {/* Left Panel - NFT Preview */}
        <div className="modal-preview-panel">
          <div className="preview-header">
            <h3>Listing</h3>
            {isSFT && <span className="sft-tag"><FaCube /> SFT</span>}
          </div>

          <div className="preview-single-item">
            <div className="preview-image-wrapper">
              {nft.image ? (
                <img src={nft.image} alt={nft.name || 'NFT'} className="preview-image" />
              ) : (
                <div className="preview-placeholder-large">
                  <FaImage />
                </div>
              )}
            </div>

            <div className="preview-item-details">
              <span className="item-name">{nft.name || `#${nft.nftId}`}</span>
              <span className="item-collection">{nft.collectionId}</span>
              {isSFT && (
                <span className="item-balance">
                  <FaCube /> Balance: {maxQuantity}
                </span>
              )}
            </div>
          </div>

          {/* Price Preview */}
          {(price || startingBid) && (
            <div className="preview-price-summary">
              <div className="price-preview-row">
                <span>Listing Price</span>
                <span className="price-value">
                  {listingType === 'fixed' ? price : startingBid} KLV
                  {isSFT && quantity > 1 && ' each'}
                </span>
              </div>
              {isSFT && quantity > 1 && (
                <div className="price-preview-row total">
                  <span>Total ({quantity} units)</span>
                  <span className="price-value">
                    {(quantity * parseFloat(listingType === 'fixed' ? price : startingBid || 0)).toFixed(2)} KLV
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Listing Form */}
        <div className="modal-form-panel">
          <div className="form-header">
            <h2>
              {listingType === 'fixed' ? <FaTag /> : <FaGavel />}
              {listingType === 'fixed' ? ' Fixed Price' : ' Auction'}
            </h2>
            <button className="close-btn" onClick={onClose} disabled={loading}>
              <FaTimes />
            </button>
          </div>

          <div className="form-content">
            {/* Listing Type Tabs */}
            <div className="listing-type-tabs">
              <button
                className={`tab-btn ${listingType === 'fixed' ? 'active' : ''}`}
                onClick={() => setListingType('fixed')}
                disabled={loading}
              >
                <FaTag />
                <span>Fixed Price</span>
              </button>
              <button
                className={`tab-btn ${listingType === 'auction' ? 'active' : ''}`}
                onClick={() => setListingType('auction')}
                disabled={loading}
              >
                <FaGavel />
                <span>Auction</span>
              </button>
            </div>

            {/* SFT Quantity */}
            {isSFT && (
              <div className="form-group">
                <label><FaCube /> Quantity to List</label>
                <div className="quantity-controls-inline">
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(quantity - 1)}
                    disabled={quantity <= 1 || loading}
                  >
                    <FaMinus />
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    min="1"
                    max={maxQuantity}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => handleQuantityChange(quantity + 1)}
                    disabled={quantity >= maxQuantity || loading}
                  >
                    <FaPlus />
                  </button>
                  <span className="max-label">/ {maxQuantity}</span>
                </div>
              </div>
            )}

            {/* Price Inputs */}
            {listingType === 'fixed' ? (
              <div className="form-group">
                <label>
                  <FaCoins /> Price {isSFT && '(per unit)'}
                </label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    placeholder="0.00"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    step="0.01"
                    min="0"
                    disabled={loading}
                  />
                  <span className="suffix">KLV</span>
                </div>
                {isSFT && price && quantity > 1 && (
                  <span className="input-hint">
                    Total: {quantity} × {price} = {(quantity * parseFloat(price)).toFixed(2)} KLV
                  </span>
                )}
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label><FaGavel /> Starting Bid</label>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      placeholder="0.00"
                      value={startingBid}
                      onChange={(e) => setStartingBid(e.target.value)}
                      step="0.01"
                      min="0"
                      disabled={loading}
                    />
                    <span className="suffix">KLV</span>
                  </div>
                  <span className="input-hint">Bidding starts at this price</span>
                </div>

                <div className="form-group">
                  <label>
                    <FaBolt /> Buy Now <span className="optional-tag">Optional</span>
                  </label>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      placeholder="0.00"
                      value={buyNowPrice}
                      onChange={(e) => setBuyNowPrice(e.target.value)}
                      step="0.01"
                      min="0"
                      disabled={loading}
                    />
                    <span className="suffix">KLV</span>
                  </div>
                  <span className="input-hint">Instant win price</span>
                </div>

                <div className="auction-info-box">
                  <FaInfoCircle />
                  <div>
                    <strong>How auctions work:</strong>
                    <ul>
                      <li>Bidders compete from {startingBid || '0'} KLV</li>
                      {buyNowPrice && <li>Bid {buyNowPrice}+ KLV = instant win</li>}
                      <li>Accept highest bid when auction ends</li>
                    </ul>
                  </div>
                </div>
              </>
            )}

            {/* Duration */}
            <div className="form-group">
              <label><FaClock /> Duration</label>
              <div className="duration-options">
                {durationOptions.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`duration-btn ${duration === opt.value ? 'active' : ''}`}
                    onClick={() => {
                      setDuration(opt.value);
                      // Auto-disable Top Choice when duration is not 1 day
                      if (opt.value !== 1) {
                        setTopChoice(false);
                      }
                    }}
                    disabled={loading}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <span className="input-hint">
                <FaClock /> Ends: {calculateEndDate()}
              </span>
            </div>

            {/* Top Choice - Only available for 1 day listings */}
            <div className={`form-group featured ${duration !== 1 ? 'disabled' : ''}`}>
              <label className="toggle-row">
                <div className="toggle-info">
                  <span className="toggle-title">⭐ Top Choice</span>
                  <span className="toggle-desc">
                    {duration === 1
                      ? 'Feature your listing (+50 KLV)'
                      : 'Only available for 1 day listings'}
                  </span>
                </div>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={topChoice && duration === 1}
                    onChange={(e) => setTopChoice(e.target.checked)}
                    disabled={loading || duration !== 1}
                  />
                  <span className="toggle-slider"></span>
                </div>
              </label>
            </div>

            {/* Fee Breakdown */}
            <div className="fee-breakdown">
              <div className="fee-row">
                <span>Network Fee</span>
                <span>{useKdaFee ? 'Paid with KDA' : '~2 KLV'}</span>
              </div>
              {topChoice && (
                <div className="fee-row highlight">
                  <span>Top Choice Fee</span>
                  <span>50 KLV</span>
                </div>
              )}
              <div className="fee-row total">
                <span>Total Fees</span>
                <span>{topChoice ? (useKdaFee ? '50 KLV' : '~52 KLV') : (useKdaFee ? '0 KLV' : '~2 KLV')}</span>
              </div>
            </div>

            {/* Error */}
            {errorMsg && (
              <div className="status-banner error">
                <FaTimesCircle /> {errorMsg}
              </div>
            )}

            {/* Status */}
            {status && status !== 'error' && (
              <div className={`status-banner ${status}`}>
                {status === 'processing' && <><FaSpinner className="spin" /> Processing...</>}
                {status === 'success' && <><FaCheckCircle /> Listed successfully!</>}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button className="btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleList} disabled={loading}>
              {loading ? (
                <><FaSpinner className="spin" /> Processing...</>
              ) : (
                <>
                  {listingType === 'fixed' ? <FaTag /> : <FaGavel />}
                  {listingType === 'fixed' ? ' List for Sale' : ' Start Auction'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuctionListingModal;
